import { HTTPError } from '../support/errors';

import { apiRequest, msalInstance } from './authConfig';

const API_BASE = process.env.REACT_APP_API_HOST ?? '';
const AGENTIC_API_BASE = process.env.REACT_APP_AGENTIC_API_HOST ?? '';
// LOCAL DEV ONLY — bypass auth when REACT_APP_SKIP_AUTH=1. STRIP BEFORE STAGING/PRODUCTION DEPLOY.
const SKIP_AUTH = process.env.REACT_APP_SKIP_AUTH === '1';

export enum API_TYPE {
  DEFAULT = 'default',
  AGENTIC = 'agentic',
}

type ApiType = API_TYPE.DEFAULT | API_TYPE.AGENTIC;

export const getApiBase = (apiType: ApiType = API_TYPE.DEFAULT): string => {
  return apiType === API_TYPE.AGENTIC ? AGENTIC_API_BASE : API_BASE;
};

const getTokenFromMSAL = async (): Promise<string | undefined> => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    const accessTokenResponse = await msalInstance.acquireTokenSilent({
      ...apiRequest,
    });
    return accessTokenResponse.accessToken;
  }
};

export const resolveBearerToken = async (): Promise<
  string | undefined | null
> => {
  if (SKIP_AUTH) return 'dev-skip-auth';
  const cached = localStorage.getItem('msal.idtoken');
  if (cached) return cached;
  return getTokenFromMSAL();
};

async function request(
  path: string,
  options?: RequestInit,
  apiType: ApiType = API_TYPE.DEFAULT
) {
  const token = await resolveBearerToken();

  options = { ...options };
  options.headers = { Authorization: `Bearer ${token}`, ...options.headers };

  const apiBase = getApiBase(apiType);
  const url = `${apiBase}${path}`;
  const response = await fetch(url, options);

  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch {
      // Ignore a failure to parse the response
    }

    throw new HTTPError(url, response.status, response.statusText, body);
  }

  return response;
}

export async function load(
  path: string,
  signal?: RequestInit,
  apiType: ApiType = API_TYPE.DEFAULT
) {
  if (path) {
    const response = await request(path, signal, apiType);
    return await response.json();
  }
  return null;
}

export async function put<T>(
  path: string,
  payload: object,
  apiType: ApiType = API_TYPE.DEFAULT
) {
  const response = await request(
    path,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    apiType
  );

  return (await response.json()) as T;
}

export async function create<T>(
  path: string,
  payload: object,
  apiType: ApiType = API_TYPE.DEFAULT,
  init?: Pick<RequestInit, 'signal'>
) {
  const response = await request(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      ...init,
    },
    apiType
  );

  return (await response.json()) as T;
}

export const update = create;

export async function patch<T>(
  path: string,
  payload: object,
  apiType: ApiType = API_TYPE.DEFAULT
) {
  const response = await request(
    path,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    apiType
  );

  return (await response.json()) as T;
}

export async function remove(
  path: string,
  apiType: ApiType = API_TYPE.DEFAULT
) {
  await request(
    path,
    {
      method: 'DELETE',
    },
    apiType
  );
}

// Streaming POST. Caller reads response.body as a ReadableStream and parses
// the framing (e.g., NDJSON). Used by Agentic-API v2 endpoints that emit
// per-row deltas during a turn.
export async function streamRequest(
  path: string,
  payload: object,
  apiType: ApiType = API_TYPE.DEFAULT
): Promise<Response> {
  const token = await resolveBearerToken();
  const url = `${getApiBase(apiType)}${path}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch {
      // Ignore a failure to parse the response
    }
    throw new HTTPError(url, response.status, response.statusText, body);
  }

  return response;
}
